import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Receipt, Crown } from 'lucide-react';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
import { CurrencySelect } from '../components/CurrencySelect';
import { UserAvatarGroup } from '../components/UserAvatarGroup';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { createTab, subscribeToUserTabs, joinTab, getTabById } from '../services/tabs'; // Added getTabById
import { getMultipleUserProfiles, getUserProfile } from '../services/users';
import { getTabStatus, calculateBillTotal, calculateUserTotal } from '../utils/tabCalculations';
import { formatCurrency } from '../utils/currencyFormatting';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import type { Tab, UserProfile } from '../types';
import { GuestService } from '../services/guest'; // Local storage service
export const TabsPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);

  const [tabName, setTabName] = useState('');
  const [currency, setCurrency] = useState(localStorage.getItem('preferred_currency') ?? 'USD');
  const [showCompleted, setShowCompleted] = useState(false);

  const [joinId, setJoinId] = useState('');

  const [memberProfilesMap, setMemberProfilesMap] = useState<Record<string, UserProfile[]>>({});
  const [selectedTab, setSelectedTab] = useState<Tab | null>(null);



  async function fetchProfiles(currentTabs: Tab[]) {
    // Fetch user profiles for all tabs
    const profilesMap: Record<string, UserProfile[]> = {};
    const allMembers = new Set<string>();

    currentTabs.forEach(t => { t.members.forEach(uid => allMembers.add(uid)); });

    if (allMembers.size > 0) {
      const profiles = await getMultipleUserProfiles(Array.from(allMembers));
      // Map back to tabs
      currentTabs.forEach(t => {
        profilesMap[t.id] = profiles.filter(p => t.members.includes(p.uid));
      });
    }
    setMemberProfilesMap(prev => ({ ...prev, ...profilesMap }));
  }

  useEffect(() => {
    if (user) {
      // Authenticated User Flow
      const unsubscribe = subscribeToUserTabs(user.uid, async (updatedTabs) => {
        setTabs(updatedTabs);
        await fetchProfiles(updatedTabs);
        setLoading(false);
      });
      return () => { unsubscribe(); };
    } else {
      // Guest Flow
      const loadGuestTabs = async () => {
        setLoading(true);
        setTabs([]); // Ensure UI is cleared immediately to prevent stale data
        const guestTabIds = GuestService.getGuestTabs();

        if (guestTabIds.length === 0) {
          setLoading(false);
          return;
        }

        try {
          // Parallel fetch of all guest tabs
          const fetchedTabs = await Promise.all(
            guestTabIds.map(id => getTabById(id))
          );

          // Filter out nulls (deleted tabs)
          const validTabs = fetchedTabs.filter(t => t !== null);
          setTabs(validTabs);
          await fetchProfiles(validTabs);
        } catch (e) {
          console.error("Failed to load guest tabs", e);
        } finally {
          setLoading(false);
        }
      };

      void loadGuestTabs();
    }
  }, [user]);


  const handleCreateTab = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    if (!tabName.trim()) {
      showToast("Please enter a name for the tab", "error");
      return;
    }

    try {
      // Fetch user profile to get default payment config
      const profile = await getUserProfile(user.uid);

      const newTab = await createTab({
        name: tabName,
        currency,
        date: new Date().toISOString(),
        ownerId: user.uid,
        items: [],
        members: [user.uid],
        paymentConfig: profile?.paymentConfig ?? null,
        tipPercentage: 0,
        serviceChargePercentage: 0,
      });
      setIsCreateModalOpen(false);
      setTabName('');
      showToast("Tab created successfully!", "success");
      void navigate(`/tab/${newTab.id}`);
    } catch (error) {
      console.error("Error creating tab:", error);
      showToast("Failed to create tab", "error");
    }
  };

  const handleJoinTab = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    if (!joinId.trim()) {
      showToast("Please enter a tab ID", "error");
      return;
    }

    try {
      const tab = await joinTab(joinId, user.uid);
      setIsJoinModalOpen(false);
      setJoinId('');
      showToast("Joined tab successfully!", "success");
      void navigate(`/tab/${tab.id}`);
    } catch {
      showToast("Invalid ID or tab not found", "error");
    }
  };



  const activeTabs = tabs.filter(t => !t.isComplete);
  const completedTabs = tabs.filter(t => t.isComplete);

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Header */}
      {/* Fixed Full-Width Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-bg-header border-b border-border-default shadow-sm">
        <div className="max-w-md mx-auto flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-2xl font-black text-text-heading">My Tabs</h1>
            <p className="text-xs text-text-muted mt-0.5">Split bills with friends</p>
          </div>
          <div className="flex gap-2">

            <Button size="sm" variant="outline" onClick={() => { setIsJoinModalOpen(true); }}>Join</Button>
            <div className="group relative">
              <Button
                size="sm"
                onClick={() => { setIsCreateModalOpen(true); }}
                disabled={!user}
              >
                <Plus className="mr-1 h-4 w-4" />
                New Tab
              </Button>
              {!user && (
                <div className="absolute top-full right-0 mt-2 hidden w-48 rounded bg-bg-card-alt px-2 py-1 text-[10px] text-text-heading group-hover:block border border-border-default shadow-lg z-50">
                  Log in to create new tabs
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for Header */}
      <div className="h-[72px] w-full flex-none" />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {loading ? (
          <div className="h-full flex items-center justify-center pt-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : activeTabs.length === 0 && completedTabs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-full bg-bg-card-alt p-4">
              <Receipt className="h-8 w-8 text-text-faint" />
            </div>
            <h3 className="text-lg font-medium text-text-heading">No active tabs</h3>
            <p className="mt-1 text-sm text-text-muted">
              Create a tab or join one via link to start splitting.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {activeTabs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-border-default rounded-xl bg-bg-card/30">
                <p className="text-sm font-bold text-text-muted">No active tabs</p>
                <p className="text-xs text-text-faint mt-1">All your tabs are completed.</p>
              </div>
            ) : (
              activeTabs.map((tab) => {
                const registeredUsers = memberProfilesMap[tab.id] ?? [];
                const manualUsers = tab.manualMembers ?? [];
                const totalMembers = registeredUsers.length + manualUsers.length;

                return (
                  <div
                    key={tab.id}
                    className="rounded-xl border border-border-default bg-bg-card p-4 shadow-sm transition-all hover:shadow-md cursor-pointer active:scale-[0.98] group"
                    onClick={() => navigate(`/tab/${tab.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      {/* Left Side: Title, Date, Members */}
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <h3 className="font-bold text-lg text-text-heading group-hover:text-primary-600 transition-colors leading-tight">{tab.name}</h3>
                          {tab.ownerId === user?.uid && (
                            <span title="You are the owner" className="flex items-center">
                              <Crown size={14} className="text-amber-500 fill-amber-500/20" />
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-muted mb-3">
                          {new Date(tab.date).toLocaleDateString()} • {tab.items.length} items
                        </p>
                        
                        {totalMembers > 0 && (
                          <div 
                            className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTab(tab);
                              setIsMembersModalOpen(true);
                            }}
                          >
                            <UserAvatarGroup
                              registeredUsers={registeredUsers}
                              manualUsers={manualUsers}
                              maxDisplay={5}
                            />
                          </div>
                        )}
                      </div>

                      {/* Right Side: Total, Paid, Status */}
                      <div className="flex flex-col items-end shrink-0 gap-2">
                        {(() => {
                          const total = calculateBillTotal(tab);
                          const paidIds = tab.completedMembers ?? [];
                          const totalPaid = paidIds.reduce((sum, uid) => sum + calculateUserTotal(tab, uid), 0);
                          
                          return (
                            <div className="text-right">
                              <p className="text-xl font-black text-text-heading leading-none">
                                {formatCurrency(total, tab.currency)}
                              </p>
                              {totalPaid > 0 && (
                                <p className="text-[10px] text-text-faint font-medium mt-1">
                                  {formatCurrency(totalPaid, tab.currency)} paid
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        {(() => {
                          const status = getTabStatus(tab);
                          const statusColors = {
                            complete: "text-emerald-700 bg-emerald-100 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
                            all_paid: "text-blue-700 bg-blue-100 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
                            in_progress: "text-amber-700 bg-amber-100 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
                            not_started: "text-slate-600 bg-slate-100 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/20"
                          };
                          const statusText = {
                            complete: "Complete",
                            all_paid: "All Paid",
                            in_progress: "In Progress",
                            not_started: "Not Started"
                          };

                          return (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border ${statusColors[status]}`}>
                              {statusText[status]}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              }))}

            {/* Completed Tabs Section */}
            {completedTabs.length > 0 && (
              <div className="pt-2">
                <button
                  onClick={() => { setShowCompleted(!showCompleted); }}
                  className="w-full text-center py-4 text-xs font-medium text-text-muted hover:text-text-heading transition-colors"
                >
                  {showCompleted ? 'Hide' : 'Show'} {completedTabs.length} Completed Tab{completedTabs.length !== 1 ? 's' : ''}
                </button>

                {showCompleted && (
                  <div className="grid gap-4 mt-2">
                    {completedTabs.map((tab) => {
                      const registeredUsers = memberProfilesMap[tab.id] ?? [];
                      const manualUsers = tab.manualMembers ?? [];
                      const totalMembers = registeredUsers.length + manualUsers.length;

                      return (
                        <div
                          key={tab.id}
                          className="rounded-xl border border-border-default bg-bg-card/60 p-4 shadow-sm transition-all hover:shadow-md cursor-pointer active:scale-[0.98] group opacity-75 hover:opacity-100"
                          onClick={() => navigate(`/tab/${tab.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            {/* Left Side: Title, Date, Members */}
                            <div className="flex-1 pr-4">
                              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                <h3 className="font-bold text-lg text-text-heading group-hover:text-primary-600 transition-colors leading-tight">{tab.name}</h3>
                                {tab.ownerId === user?.uid && (
                                  <span title="You are the owner" className="flex items-center">
                                    <Crown size={14} className="text-amber-500 fill-amber-500/20" />
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-text-muted mb-3">
                                {new Date(tab.date).toLocaleDateString()} • {tab.items.length} items
                              </p>
                              
                              {totalMembers > 0 && (
                                <div 
                                  className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTab(tab);
                                    setIsMembersModalOpen(true);
                                  }}
                                >
                                  <UserAvatarGroup
                                    registeredUsers={registeredUsers}
                                    manualUsers={manualUsers}
                                    maxDisplay={5}
                                  />
                                </div>
                              )}
                            </div>

                            {/* Right Side: Total, Paid, Status */}
                            <div className="flex flex-col items-end shrink-0 gap-2">
                              {(() => {
                                const total = calculateBillTotal(tab);
                                const paidIds = tab.completedMembers ?? [];
                                const totalPaid = paidIds.reduce((sum, uid) => sum + calculateUserTotal(tab, uid), 0);
                                
                                return (
                                  <div className="text-right">
                                    <p className="text-xl font-black text-text-heading leading-none">
                                      {formatCurrency(total, tab.currency)}
                                    </p>
                                    {totalPaid > 0 && (
                                      <p className="text-[10px] text-text-faint font-medium mt-1">
                                        {formatCurrency(totalPaid, tab.currency)} paid
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border text-emerald-700 bg-emerald-100 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                                Complete
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); }}
        title="Start a New Tab"
      >
        <form onSubmit={handleCreateTab} className="space-y-4">
          <Input
            label="Tab Name"
            placeholder="e.g. Dinner with Friends"
            value={tabName}
            onChange={(e) => { setTabName(e.target.value); }}
          />
          <CurrencySelect
            label="Currency"
            value={currency}
            onChange={setCurrency}
          />
          <Button type="submit" className="w-full">Create Tab</Button>
        </form>
      </Modal>

      {/* Join Modal */}
      <Modal
        isOpen={isJoinModalOpen}
        onClose={() => { setIsJoinModalOpen(false); }}
        title="Join a Tab"
      >
        <form onSubmit={handleJoinTab} className="space-y-4">
          <Input
            label="Tab ID"
            placeholder="e.g. 7f2d..."
            value={joinId}
            onChange={(e) => { setJoinId(e.target.value); }}
          />
          <Button type="submit" className="w-full">Join</Button>
        </form>
      </Modal>

      {/* Members Modal */}
      <Modal
        isOpen={isMembersModalOpen}
        onClose={() => {
          setIsMembersModalOpen(false);
          setSelectedTab(null);
        }}
        title={`Members (${selectedTab ? (selectedTab.members.length + (selectedTab.manualMembers?.length ?? 0)) : 0})`}
      >
        {selectedTab && (
          <div className="space-y-3">
            {/* Registered Users */}
            {(memberProfilesMap[selectedTab.id] ?? []).map((profile) => (
              <div key={profile.uid} className="flex items-center gap-3 rounded-xl border border-border-default p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-bold">
                  {profile.displayName.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-text-heading">{profile.displayName}</p>
                  <p className="text-[10px] text-text-muted">
                    {profile.uid === selectedTab.ownerId ? "Owner" : "Member"}
                  </p>
                </div>
              </div>
            ))}

            {/* Manual Users */}
            {(selectedTab.manualMembers ?? []).map((manualUser) => (
              <div key={manualUser.id} className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 font-bold">
                  {manualUser.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-text-heading">{manualUser.name}</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">Temporary User</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};
